import { CommonModule, DecimalPipe } from '@angular/common';
import { ChangeDetectorRef, Component, ElementRef, Input, OnInit, QueryList, ViewChildren } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { PrimeNgModule } from '../shared/prime-ng.module';
import { ProjectService } from '../services/project.service';
import { values } from 'underscore';
import { Router } from '@angular/router';
import { ClientService } from '../services/client.service';

const PALETTE = [
  { color: '#1D9E75', colorLight: '#E1F5EE', colorDark: '#085041', icon: 'ti-heart' },
  { color: '#BA7517', colorLight: '#FAEEDA', colorDark: '#412402', icon: 'ti-shield-check' },
  { color: '#3B8BD4', colorLight: '#E6F1FB', colorDark: '#042C53', icon: 'ti-bus' },
  { color: '#D4537E', colorLight: '#FBEAF0', colorDark: '#4B1528', icon: 'ti-hand-helping' },
  { color: '#7F77DD', colorLight: '#EEEDFE', colorDark: '#26215C', icon: 'ti-building-community' },
  { color: '#D85A30', colorLight: '#FAECE7', colorDark: '#4A1B0C', icon: 'ti-first-aid-kit' },
];

let MOCK_RESPONSE: any = {};

@Component({
  selector: 'app-project-analytics-1',
  standalone: true,
  imports: [CommonModule, FormsModule, PrimeNgModule, DecimalPipe],
  templateUrl: './project-analytics-1.html',
  styleUrl: './project-analytics-1.css',
})
export class ProjectAnalytics1 implements OnInit {
  @ViewChildren('yearFill') yearFills!: QueryList<ElementRef>;
  grandTotal = 0;
  private rawProjects: any[] = [];
  totalActiveProject = MOCK_RESPONSE?.projects?.length;
  searchQuery = '';
  sortDesc = true;
  isLoading = true;
  startYear: any;
  endYear: any;
  minToYear: any;
  shortAmountBasedOnAcesnding = true;
  shortYearsBasedOnAcesnding = true;
  selectedFilter = 'YEAR';

  openProjectIds = new Set<string>();
  selectedProjectId: string | null = null;
  selectedSubId: string | null = null;

  get largest(): any {
    return this?.totalActiveProject ? [...this?.rawProjects].sort((a, b) => b?.amount - a?.amount)[0] : null;
  }

  get average(): number {
    return this.rawProjects.length ? Math.round(this.grandTotal / this.rawProjects.length) : 0;
  }

  get filteredProjects(): any[] {
    const q = this.searchQuery.toLowerCase();
    let list = q
      ? this.rawProjects.filter(p =>
        p.name.toLowerCase().includes(q) ||
        p.subProjects.some((s: any) => s.name.toLowerCase().includes(q))
      )
      : [...this.rawProjects];
    return this.totalActiveProject ? list.sort((a, b) => this.sortDesc ? b.amount - a.amount : a.amount - b.amount) : [];
  }

  get selectedProject(): any {
    return this.rawProjects.find(p => p.project_id === this.selectedProjectId) ?? null;
  }

  get selectedSub(): any {
    const p = this.selectedProject;
    if (!p || !this.selectedSubId) return null;
    return p.subProjects.find((s: any) => s.form_id === this.selectedSubId) ?? null;
  }

  get detailEntity(): any {
    return this.selectedSub ?? null;
  }

  get maxYearAmount(): number {
    const e = this.detailEntity;
    if (!e || !e.years.length) return 1;
    return Math.max(...e.years.map((y: any) => y.amount));
  }

  ngOnInit(): void {
    this.isLoading = true;
    this.projectService.getMasterData();
    this.getData();
  }

  constructor(public projectService: ProjectService,
    private clientService: ClientService,
    public router: Router,
    public cdr: ChangeDetectorRef
  ) {
  }

  ngAfterViewInit() {
    // Trigger animation after view is rendered
    setTimeout(() => {
      this.yearFills.forEach((fill, index) => {
        const element = fill.nativeElement;
        const width = element.style.width;
        element.style.width = '0%';
        setTimeout(() => {
          element.style.width = width;
        }, 100 + (index * 100));
      });
    }, 100);
  }

  getData() {
    const userCategoryId = sessionStorage.getItem('user_category_id');
    const userCode = sessionStorage.getItem('user_code');
    let payload = {
      "user_code": userCode,
      "user_category_id": userCategoryId,
      "from_year": this.startYear ? new Date(this.startYear)?.getFullYear() : null,
      "to_year": this.endYear ? new Date(this.endYear)?.getFullYear() : null
    }
    this.clientService.postAPI('/csr/form/project/analytics/', payload).subscribe((res: any) => {
      if (res && res.data && res.code == 'CSR_MS_OK_200') {
        MOCK_RESPONSE = res.data;
        this.totalActiveProject = MOCK_RESPONSE?.projects?.length;
        if (this.totalActiveProject) {
          this.loadData();
        } else {
          this.totalActiveProject = 0;
          this.grandTotal = 0;
        }
        this.cdr.detectChanges();
        this.cdr.markForCheck();
      } else {
        this.isLoading = false;
      }
    }, (error: any) => {
      this.isLoading = false;
    })
  }

  private loadData(): void {
    setTimeout(() => this.handleResponse(MOCK_RESPONSE), 400);
  }

  private handleResponse(res: any): void {
    this.grandTotal = res?.totalAmount;
    this.rawProjects = res?.projects?.map((p: any, i: number) => ({ ...p, ...PALETTE[i % PALETTE.length] }));
    const data = this.filteredProjects;
    this.onClickProject(data[0]?.project_id)
    this.onClickSub(data[0]?.project_id, data[0]?.subProjects[0]?.form_id)
    setTimeout(() => {
      this.isLoading = false;
    }, 200)
  }

  fmt(n: number): string {
    return this.totalActiveProject ? '₹ ' + n?.toLocaleString('en-IN') : '₹ 0';
  }

  pct(amount: number, total: number): string {
    return total ? ((amount / total) * 100)?.toFixed(1) : '0.0';
  }

  yearBarWidth(amount: number): string {
    const maxAmount = this.detailEntity?.amount > 0 ? this.detailEntity?.amount : 1;
    return (amount / maxAmount * 100)?.toFixed(2) + '%';
  }

  isOpen(projectId: string): boolean {
    return this?.openProjectIds?.has(projectId);
  }

  isSubSelected(subId: string): boolean {
    return this.selectedSubId === subId;
  }

  isProjectActive(projectId: string): boolean {
    return this.selectedProjectId === projectId;
  }

  isSubView(): boolean {
    return !!this.selectedSub;
  }

  onClickProject(projectId: string, project?: any): void {
    const current = new Set("");
    if (current.has(projectId)) {
      current.delete(projectId);
    } else {
      current.add(projectId);
    }
    this.openProjectIds = current;
    this.selectedProjectId = projectId;
    this.selectedSubId = null;
    this.onClickSub(projectId, project?.subProjects[0]?.form_id)
  }

  onClickSub(projectId: string, subId: string, event?: Event): void {
    event?.stopPropagation();
    this.selectedProjectId = projectId;
    this.selectedSubId = subId;
    const current = new Set(this.openProjectIds);
    current.add(projectId);
    this.openProjectIds = current;
    setTimeout(() => {
      if (this.selectedFilter === 'YEAR') {
        this.shortByYears('changes');
      } else {
        this.shortByAmount('changes');
      }
    }, 500);
  }

  backToProject(): void {
    this.selectedSubId = null;
  }

  toggleSort(): void {
    this.sortDesc = !this.sortDesc;
  }

  getToDate(event: any) {
    if (this.startYear) {
      this.minToYear = new Date(this.startYear.getFullYear(), 0, 1);
      if (this.endYear && this.endYear < this.minToYear) {
        this.endYear = null;
      }
    }
  }

  async getReportData(data: any, year: any) {
    const projectList = await this.projectService.getProjectMaster();
    const selectedProjectData: any = projectList.filter((e) => {
      return e.project_id == this.selectedProject?.project_id;
    })
    const reportData = {
      form_id: data?.form_id,
      values: [{ field_id: year?.field_id, values: year?.year?.toString() }],
      selected_project: selectedProjectData[0]
    }
    if (selectedProjectData.length > 0) {
      this.projectService.setSelectedProject(selectedProjectData[0]);
      this.projectService.getMasterData();
      setTimeout(() => {
        this.projectService.reportData.set(reportData);
        this.router.navigate(['form_reports']);
      }, 400);
    }
  }

  searchDataOnYear() {
    let y1 = new Date(this.startYear);
    let y2 = new Date(this.endYear);
    if (y1 !== null && y2 !== null) {
      this.getData();
    }
  }

  clearFilter() {
    this.startYear = null;
    this.endYear = null;
    this.getData();
  }

  shortByAmount(flag?: any) {
    if (!flag) {
      this.shortAmountBasedOnAcesnding = !this.shortAmountBasedOnAcesnding;
    }
    this.selectedFilter = 'AMOUNT';
    if (this.shortAmountBasedOnAcesnding) {
      this.detailEntity.years = this.detailEntity?.years?.sort((a: any, b: any) => a.amount - b.amount)
    } else {
      this.detailEntity.years = this.detailEntity?.years?.sort((a: any, b: any) => b.amount - a.amount)
    }
  }

  shortByYears(flag?: any) {
    if (!flag) {
      this.shortYearsBasedOnAcesnding = !this.shortYearsBasedOnAcesnding;
    }
    this.selectedFilter = 'YEAR';
    if (this.shortYearsBasedOnAcesnding) {
      this.detailEntity.years = this.detailEntity?.years?.sort((a: any, b: any) => a.year - b.year)
    } else {
      this.detailEntity.years = this.detailEntity?.years?.sort((a: any, b: any) => b.year - a.year)
    }
  }
}